import { useState, useCallback, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number // milliseconds, 0 means permanent
  action?: {
    label: string
    onClick: () => void
  }
  timestamp: Date
  read?: boolean
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
}

// Global notification state
let globalNotifications: Notification[] = []
let globalListeners: Array<(notifications: Notification[]) => void> = []

export function __resetNotificationsForTests() {
  globalNotifications = []
  globalListeners = []
}

const addGlobalNotification = (notification: Notification) => {
  globalNotifications = [notification, ...globalNotifications]
  globalListeners.forEach(listener => listener(globalNotifications))
}

const removeGlobalNotification = (id: string) => {
  globalNotifications = globalNotifications.filter(n => n.id !== id)
  globalListeners.forEach(listener => listener(globalNotifications))
}

const markGlobalNotificationRead = (id: string) => {
  globalNotifications = globalNotifications.map(n => 
    n.id === id ? { ...n, read: true } : n
  )
  globalListeners.forEach(listener => listener(globalNotifications))
}

const clearGlobalNotifications = () => {
  globalNotifications = []
  globalListeners.forEach(listener => listener(globalNotifications))
}

// Notification hook
export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>(globalNotifications)
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map())

  useEffect(() => {
    // Subscribe to global notification changes
    const listener = (newNotifications: Notification[]) => {
      setNotifications(newNotifications)
    }
    
    globalListeners.push(listener)
    
    return () => {
      globalListeners = globalListeners.filter(l => l !== listener)
      
      // Clear all timeouts
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout))
      timeoutRefs.current.clear()
    }
  }, [])

  const showNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const id = Math.random().toString(36).substring(2, 15)
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
      duration: notification.duration ?? 5000 // Default 5 seconds
    }

    addGlobalNotification(newNotification)

    // Auto-remove notification after duration
    if (newNotification.duration > 0) {
      const timeout = setTimeout(() => {
        removeGlobalNotification(id)
        timeoutRefs.current.delete(id)
      }, newNotification.duration)
      
      timeoutRefs.current.set(id, timeout)
    }

    return id
  }, [])

  const removeNotification = useCallback((id: string) => {
    // Clear timeout if exists
    const timeout = timeoutRefs.current.get(id)
    if (timeout) {
      clearTimeout(timeout)
      timeoutRefs.current.delete(id)
    }
    
    removeGlobalNotification(id)
  }, [])

  const markAsRead = useCallback((id: string) => {
    markGlobalNotificationRead(id)
  }, [])

  const clearAll = useCallback(() => {
    // Clear all timeouts
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout))
    timeoutRefs.current.clear()
    
    clearGlobalNotifications()
  }, [])

  const unreadCount = notifications.filter(n => !n.read).length

  // Helper methods for common notification types
  const showSuccess = useCallback((title: string, message: string, options?: Partial<Notification>) => {
    return showNotification({ 
      type: 'success', 
      title, 
      message, 
      ...options 
    })
  }, [showNotification])

  const showError = useCallback((title: string, message: string, options?: Partial<Notification>) => {
    return showNotification({ 
      type: 'error', 
      title, 
      message, 
      duration: 0, // Errors persist until manually dismissed
      ...options 
    })
  }, [showNotification])

  const showWarning = useCallback((title: string, message: string, options?: Partial<Notification>) => {
    return showNotification({ 
      type: 'warning', 
      title, 
      message, 
      duration: 8000, // Warnings last longer
      ...options 
    })
  }, [showNotification])

  const showInfo = useCallback((title: string, message: string, options?: Partial<Notification>) => {
    return showNotification({ 
      type: 'info', 
      title, 
      message, 
      ...options 
    })
  }, [showNotification])

  return {
    notifications,
    unreadCount,
    showNotification,
    removeNotification,
    markAsRead,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }
}

// Hook for real-time conflict detection
export function useConflictDetection() {
  const { showWarning, showError } = useNotifications()
  const queryClient = useQueryClient()
  const activeEditsRef = useRef<Map<string, { timestamp: Date, userId: string }>>(new Map())

  const detectConflict = useCallback((resourceType: string, resourceId: string, currentUserId: string) => {
    const key = `${resourceType}:${resourceId}`
    const existingEdit = activeEditsRef.current.get(key)
    
    if (existingEdit && existingEdit.userId !== currentUserId) {
      const timeDiff = Date.now() - existingEdit.timestamp.getTime()
      
      // If someone else edited within the last 30 seconds, show conflict warning
      if (timeDiff < 30000) {
        showWarning(
          'Editing Conflict Detected',
          `Another user is currently editing this ${resourceType}. Your changes may overwrite theirs.`,
          {
            action: {
              label: 'Refresh Data',
              onClick: () => {
                queryClient.invalidateQueries({ queryKey: [resourceType, resourceId] })
              }
            }
          }
        )
        return true
      }
    }
    
    return false
  }, [showWarning, queryClient])

  const recordEdit = useCallback((resourceType: string, resourceId: string, userId: string) => {
    const key = `${resourceType}:${resourceId}`
    activeEditsRef.current.set(key, {
      timestamp: new Date(),
      userId
    })
    
    // Clean up old edits after 5 minutes
    setTimeout(() => {
      activeEditsRef.current.delete(key)
    }, 5 * 60 * 1000)
  }, [])

  const handleOptimisticUpdate = useCallback((
    resourceType: string, 
    resourceId: string, 
    updateFn: () => Promise<unknown>,
    rollbackFn?: () => void
  ) => {
    // Apply optimistic update immediately
    const optimisticResult = updateFn()
    
    // Handle potential conflicts
    optimisticResult.catch((error) => {
      if (rollbackFn) {
        rollbackFn()
      }
      
      if (error?.code === 'PGRST116') { // Postgres conflict error
        showError(
          'Update Conflict',
          'Your changes conflict with recent updates. The data has been refreshed.',
          {
            action: {
              label: 'Retry',
              onClick: () => updateFn()
            }
          }
        )
      } else {
        showError(
          'Update Failed',
          error?.message || 'Failed to save changes. Please try again.'
        )
      }
    })
    
    return optimisticResult
  }, [showError])

  return {
    detectConflict,
    recordEdit,
    handleOptimisticUpdate
  }
}

// Hook for capacity monitoring notifications
export function useCapacityMonitoring() {
  const { showWarning, showInfo } = useNotifications()

  const checkCapacityAlert = useCallback((
    facilityName: string,
    currentBookings: number,
    maxCapacity: number,
    thresholds = { warning: 0.8, critical: 0.95 }
  ) => {
    const utilization = currentBookings / maxCapacity
    
    if (utilization >= thresholds.critical) {
      showWarning(
        'Capacity Critical',
        `${facilityName} is at ${Math.round(utilization * 100)}% capacity (${currentBookings}/${maxCapacity})`,
        {
          duration: 0, // Persist until dismissed
          action: {
            label: 'View Schedule',
            onClick: () => {
              window.location.href = '/calendar'
            }
          }
        }
      )
    } else if (utilization >= thresholds.warning) {
      showInfo(
        'High Capacity',
        `${facilityName} is at ${Math.round(utilization * 100)}% capacity (${currentBookings}/${maxCapacity})`,
        {
          duration: 10000
        }
      )
    }
    
    return utilization
  }, [showWarning, showInfo])

  const checkRevenueThreshold = useCallback((
    currentRevenue: number,
    targetRevenue: number,
    period: string = 'today'
  ) => {
    const revenueRatio = currentRevenue / targetRevenue
    
    if (revenueRatio >= 1.0) {
      showInfo(
        'Revenue Target Achieved!',
        `${period}'s revenue target of $${targetRevenue} has been reached. Current: $${currentRevenue}`,
        {
          duration: 8000
        }
      )
    } else if (revenueRatio >= 0.9) {
      showInfo(
        'Close to Revenue Target',
        `${Math.round(revenueRatio * 100)}% of ${period}'s revenue target achieved ($${currentRevenue}/$${targetRevenue})`,
        {
          duration: 6000
        }
      )
    }
    
    return revenueRatio
  }, [showInfo])

  return {
    checkCapacityAlert,
    checkRevenueThreshold
  }
}

// =============================================================
// SESSION MEDIA NOTIFICATION HOOKS
// For tracking when coaches upload new training content
// =============================================================

export interface SessionMediaNotification {
  id: string
  organization_id: string
  session_media_id: string
  client_id: string
  notification_type: string
  title: string
  message: string
  media_type: 'video' | 'voice_note' | 'document' | 'image' | 'note'
  is_read: boolean
  read_at: string | null
  sent_email: boolean
  email_sent_at: string | null
  created_at: string
}

/**
 * Fetch all session media notifications for the current user
 * Ordered by most recent first
 */
export function useSessionMediaNotifications() {
  return useQuery({
    queryKey: ['session-media-notifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('session_media_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) {
        console.error('Error fetching session media notifications:', error)
        throw error
      }

      return (data || []) as SessionMediaNotification[]
    },
    staleTime: 5000, // 5 seconds
  })
}

/**
 * Fetch unread session media notification count
 */
export function useSessionMediaUnreadCount() {
  return useQuery({
    queryKey: ['session-media-notifications', 'unread-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('session_media_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('is_read', false)

      if (error) {
        console.error('Error fetching unread count:', error)
        throw error
      }

      return count || 0
    },
    staleTime: 5000, // 5 seconds
  })
}

/**
 * Mark a single session media notification as read
 */
export function useMarkMediaNotificationAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('session_media_notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', notificationId)

      if (error) {
        console.error('Error marking notification as read:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-media-notifications'] })
      queryClient.invalidateQueries({ queryKey: ['session-media-notifications', 'unread-count'] })
    },
    onError: (error) => {
      console.error('Failed to mark notification as read:', error)
    }
  })
}

/**
 * Mark all session media notifications as read
 */
export function useMarkAllMediaNotificationsAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('session_media_notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('is_read', false)

      if (error) {
        console.error('Error marking all as read:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-media-notifications'] })
      queryClient.invalidateQueries({ queryKey: ['session-media-notifications', 'unread-count'] })
    },
    onError: (error) => {
      console.error('Failed to mark all as read:', error)
    }
  })
}
